import { motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';

import Address from '1_components/Address/Address';
import AnimatedText from '1_components/AnimatedText/AnimatedText';
import useClientSideRouting from 'state/clientSideRoutingStore';
import useScriptStore from 'state/scriptStore';

import S from './VoiceScript.module.scss';

import useKaraokeText from 'hooks/useKaraokeText';

export interface VoiceScriptProps {}

const PATH_TRANSPARENCY_MAP: Record<string, number> = {
  'age-brackets': 0.5,
  tiles: 0.5,
  weather: 0.5,
  peril_1: 0.5,
  peril_2: 0.5,
  landmark: 0.5,
  contractors: 0.5,
  'timeline-screen': 0.3,
  reels: 0.3,
  building_1: 0.3,
  building_2: 0.3,
  building_3: 0.3,
};

const VoiceScript: React.FC<VoiceScriptProps> = () => {
  const { wordsChunk, wordsSaid } = useKaraokeText();
  const setReportMentioned = useScriptStore(s => s.setReportMentioned);
  const experiencePath = useClientSideRouting(s => s.path);

  const REPORT_MENTIONED = useMemo(
    () =>
      wordsSaid.filter(w =>
        [
          'report',
          ',report',
          'report,',
          'report.',
          'report?',
          'report!',
        ].includes(w?.toLowerCase()?.trim())
      ).length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(wordsSaid)]
  );

  useEffect(() => {
    setReportMentioned(REPORT_MENTIONED);
  }, [REPORT_MENTIONED, setReportMentioned]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      className={S.HomeCaptions}
    >
      <motion.div
        className={S.GradientBright}
        style={{
          background: `radial-gradient(circle, rgba(0, 0, 0, ${
            PATH_TRANSPARENCY_MAP[experiencePath] ?? 0
          }) 0%, transparent 75%)`,
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.4, 0.75],
          scaleY: 0.4,
          scaleX: [1.3, 1],
        }}
        transition={{ repeat: Infinity, repeatType: 'mirror', duration: 6 }}
      />

      <div className={S.GradientDark} />

      <Address className={S.Address} />
      <div className={S.AnimatedTextWrapper}>
        <AnimatedText
          words={wordsChunk}
          wordsSaid={wordsSaid}
          className={S.AnimatedText}
        />
      </div>
    </motion.div>
  );
};

export default VoiceScript;
